import React, { useMemo, useState } from "react";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import Autocomplete from "src/components/form/fields5/Autocomplete";
import Input from "src/components/form/Input";
import ProjectAutocomplete from "./ProjectAutocomplete";
import getBillingLabel from "src/utils/billing/getBillingLabel";

const REQUIRED_MESSAGE = "Required";
const FLAT_RATE_BILLING_TYPE = "Flat Rate/As Quoted (Internal Work Orders)";

const getWorkOrderTypeValue = internalWorkOrderType =>
  typeof internalWorkOrderType === "string"
    ? internalWorkOrderType
    : internalWorkOrderType?.value;

const getInternalWorkOrderSchema = ({
  isCartContext,
  isProjectContext,
  internalWorkOrderTypes,
  internalBillingTypes,
  shouldShowCostCenterField,
}) => ({
    warehouse: isCartContext
      ? Yup.mixed().nullable()
      : Yup.mixed().nullable().required(REQUIRED_MESSAGE),
    destinationWarehouse: Yup.mixed().nullable().when("internalWorkOrderType", {
      is: value => getWorkOrderTypeValue(value) === "warehouse_transfer",
      then: schema => schema.required(REQUIRED_MESSAGE),
      otherwise: schema => schema.nullable(),
    }),
    internalWorkOrderType:
      internalWorkOrderTypes.length > 0
        ? Yup.string().nullable().required(REQUIRED_MESSAGE)
        : Yup.string().nullable(),
    projectId:
      isCartContext || isProjectContext
        ? Yup.mixed().nullable()
        : Yup.mixed().nullable().required(REQUIRED_MESSAGE),
    costCenter: Yup.mixed().nullable().when("billingType", {
      is: billingType =>
        shouldShowCostCenterField && billingType && billingType !== "Not Applicable",
      then: schema => schema.required(REQUIRED_MESSAGE),
      otherwise: schema => schema.nullable(),
    }),
    deadlineDate: Yup.mixed().nullable().required(REQUIRED_MESSAGE),
    comment: Yup.string().trim().required(REQUIRED_MESSAGE),
    billingType:
      internalBillingTypes.length > 0
        ? Yup.string().nullable().required(REQUIRED_MESSAGE)
        : Yup.string().nullable(),
    flatRateAmount: Yup.mixed().nullable().when("billingType", {
      is: FLAT_RATE_BILLING_TYPE,
      then: schema => schema.required(REQUIRED_MESSAGE),
      otherwise: schema => schema.nullable(),
    }),
  });

export default function InternalWorkOrderForm({
  projectId,
  warehouse,
  warehouses,
  allWarehouses,
  costCenters,
  internalWorkOrderTypes,
  internalBillingTypes,
  isCartContext,
  isProjectContext,
  onSubmit,
  onClose,
  isSubmitting,
}) {
  const [open, setOpen] = useState(false);
  const warehouseOptions = useMemo(() => warehouses || [], [warehouses]);
  const allWarehouseOptions = useMemo(() => allWarehouses || warehouses || [], [allWarehouses, warehouses]);
  const defaultWarehouseOption = useMemo(
    () => warehouseOptions.find(w => w.warehouseId === warehouse) || null,
    [warehouseOptions, warehouse]
  );
  const shouldShowCostCenterField = costCenters?.length > 1;
  const autoSelectedCostCenter = costCenters?.length === 1 ? costCenters[0] : null;
  const validationSchema = Yup.object().shape(
    getInternalWorkOrderSchema({
      isCartContext,
      isProjectContext,
      internalWorkOrderTypes,
      internalBillingTypes,
      shouldShowCostCenterField,
    })
  );

  return (
    <Formik
      enableReinitialize
      validationSchema={validationSchema}
      initialValues={{
        warehouse: defaultWarehouseOption,
        destinationWarehouse: null,
        internalWorkOrderType: "",
        projectId,
        costCenter: autoSelectedCostCenter,
        deadlineDate: null,
        comment: "",
        billingType: "",
        flatRateAmount: "",
      }}
      onSubmit={onSubmit}
    >
      {({
        values,
        errors,
        touched,
        submitCount,
        setFieldTouched,
        setFieldValue,
        submitForm,
        isSubmitting: formSubmitting,
      }) => {
        const workOrderTypeValue = getWorkOrderTypeValue(values.internalWorkOrderType);
        const showDestinationWarehouse = workOrderTypeValue === "warehouse_transfer";
        const showFlatRateAmount = values.billingType === FLAT_RATE_BILLING_TYPE;
        const showCostCenterField = shouldShowCostCenterField && values.billingType && values.billingType !== 'Not Applicable';
        const deadlineDateError =
          (touched.deadlineDate || submitCount > 0) && errors.deadlineDate ? errors.deadlineDate : "";

        return (
          <Form>
            <DialogContent>
              {!isCartContext && (
                <Box mt={1.5}>
                  <Autocomplete
                    name="warehouse"
                    label="Assigned Warehouse"
                    options={warehouseOptions}
                    getOptionLabel={option => option?.name || ""}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  />
                </Box>
              )}
              {internalWorkOrderTypes.length > 0 && (
                <Box mt={2}>
                  <Autocomplete
                    key={`work-order-type-${internalWorkOrderTypes.length}`}
                    name="internalWorkOrderType"
                    label="Internal Work Order Type"
                    options={internalWorkOrderTypes}
                    getOptionLabel={option => {
                      const str = typeof option === 'string' ? option : (option?.value || "");
                      return str.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                    }}
                    isOptionEqualToValue={(option, value) => {
                      const optionVal = typeof option === 'string' ? option : option?.value;
                      const valueVal = typeof value === 'string' ? value : value?.value;
                      return optionVal === valueVal;
                    }}
                    onChange={(e, newValue) => {
                      const valueToStore = typeof newValue === 'string' ? newValue : newValue?.value;
                      setFieldValue('internalWorkOrderType', valueToStore);
                    }}
                    freeSolo
                    forcePopupIcon
                  />
                </Box>
              )}
              {showDestinationWarehouse && (
                <Box mt={2}>
                  <Autocomplete
                    key={`destination-warehouse-${allWarehouseOptions.length}`}
                    name="destinationWarehouse"
                    label="Destination Warehouse"
                    options={allWarehouseOptions}
                    getOptionLabel={option => option?.name || ""}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  />
                </Box>
              )}
              {!isCartContext && !isProjectContext && (
                <Box mt={2}>
                  <ProjectAutocomplete name="projectId" />
                </Box>
              )}
              {internalBillingTypes && internalBillingTypes.length > 0 && (
                <Box mt={2}>
                  <Autocomplete
                    key={`billing-type-${internalBillingTypes.length}`}
                    name="billingType"
                    label="Billing Type"
                    options={internalBillingTypes}
                    getOptionLabel={option => getBillingLabel(option) || option}
                    isOptionEqualToValue={(option, value) => option === value}
                    forcePopupIcon
                  />
                </Box>
              )}
              {showFlatRateAmount && (
                <Box mt={2}>
                  <Input 
                    name="flatRateAmount" 
                    type="number" 
                    step="0.01"
                    label="Flat Rate Amount" 
                    required
                  />
                </Box>
              )}
              {showCostCenterField && (
                <Box mt={2}>
                  <Autocomplete
                    key={`cost-center-${costCenters.length}`}
                    name="costCenter"
                    label="Cost Center"
                    options={costCenters}
                    getOptionLabel={option => option?.costCenterName || ""}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    required
                    forcePopupIcon
                  />
                </Box>
              )}
            <Box mt={2}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  open={open}
                  onOpen={() => setOpen(true)}
                  onClose={() => setOpen(false)}
                  label="Complete By Date"
                  value={values.deadlineDate}
                  onChange={date => {
                    setFieldValue("deadlineDate", date);
                    setFieldTouched("deadlineDate", true, false);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      onClick: () => setOpen(true),
                      onBlur: () => setFieldTouched("deadlineDate", true, false),
                      error: !!deadlineDateError,
                      helperText: deadlineDateError,
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>
            <Box mt={2}>
              <Input name="comment" type="text" label="Comments" showNAButton={false} />
            </Box>
          </DialogContent>
          <DialogActions>
            <Box p={2} display="flex" justifyContent="flex-end" gap={1} width="100%">
              <Button onClick={onClose} variant="outlined">
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={submitForm}
                disabled={formSubmitting || isSubmitting}
              >
                Submit
              </Button>
            </Box>
          </DialogActions>
        </Form>
      );
      }}
    </Formik>
  );
}
